'use client';

import { ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import type { CheckClassName } from './button-class-policy';

/**
 * The `ink` variant is the dark gradient primary used across AI Scribe — the
 * session banners, the header Import CTA, the inline capture control, the
 * binding/override dialog footers and the Scribe home hero (VALE-534).
 *
 * It lives here, not in a feature directory, because the previous arrangement
 * proved it could not stay in one: a shared class-string const
 * (`ai-scribe/session/scribe-buttons.ts`) was introduced to stop the gradient
 * drifting, and five call sites then hand-copied the string past it anyway,
 * arriving at four different heights and two different type sizes. Owning the
 * treatment as a variant is what makes `<Button>` usable on those surfaces at
 * all — converting only their outline/ghost secondaries would have paired a
 * standard button with a hand-rolled primary in the same row.
 *
 * It carries its own rounding (`rounded-md`), type scale (`text-12-5px`) and
 * gap through `compoundVariants`, which cva emits AFTER `size` — a plain
 * variant string would have its `rounded-md` won back by `size`'s `rounded-sm`.
 */

/**
 * ── THE AXES BESIDE `variant` AND `size` (VALE-1202) ────────────────────────
 *
 * `buttonVariants` is the single source of truth for every button style, and a
 * call site expresses what it wants in props. A literal class string at a call
 * site is the antipattern this table exists to remove: it puts part of the
 * design somewhere nobody can see it holistically, so the next call site copies
 * it, approximates it, or quietly diverges.
 *
 * The axes below were derived by measurement over the 1,110 shipped `<Button>`
 * call sites, not by taste. What the 620 class strings on them actually carried:
 *
 *   878 tokens  heights and icon squares  -> `size`, extended below
 *   616 tokens  icon gap, THREE distinct  -> `gap`
 *   373 tokens  foreground + hover pairs  -> `tone`
 *   239 tokens  type scale                -> `density`
 *   147 tokens  `w-full`                  -> `block`
 *    68 tokens  the selected chip state   -> `selected`
 *    49 tokens  font weight               -> `weight`
 *    41 tokens  content alignment         -> `align`
 *   354 tokens  margins, `shrink-0`, positioning
 *
 * That last group is deliberately NOT an axis. A margin is the button's place in
 * its parent's layout, not a property of the button, and a `margin` axis would
 * just be sixteen Tailwind values behind a prop with a name that lies about what
 * it configures. Those move to a wrapper element at the call site.
 *
 * `default` on every axis is EMPTY, never a restatement of the base's own
 * `text-sm gap-1 justify-center`. Adding an axis has to be a no-op for the 526
 * call sites that never asked for it, and `button.test.tsx` asserts the class
 * string is byte-identical with each axis at its default.
 *
 * WHY `density` AND `gap` ARE SEPARATE, having started as one step: the compact
 * register originally carried `text-12-5px gap-1.5` together, because that is
 * the pair the reference call site had hand-written. The first hop of the
 * fan-out found a counter-example — `New document template` on the Documents
 * toolbar took the wider icon gap and deliberately NOT the 12.5px type, because
 * VALE-758 had chosen h-9 and 14px there so the create action would stop
 * reading as a fourth filter chip.
 *
 * THAT COUNTER-EXAMPLE IS GONE (VALE-1243): every primary add/create CTA in the
 * app now carries `size="xs" density="compact" gap="standard"`, the Documents
 * toolbar included. A create action is distinguished by being the solid one in
 * its row, not by being taller. The two axes stay separate anyway — they
 * genuinely configure different things, and collapsing them back would re-couple
 * a type scale to an icon gap for no reason beyond the call sites that happen to
 * want both today.
 *
 * ORDER IS LOAD-BEARING. cva emits base, then `variants` in key order, then
 * `compoundVariants`. `cn`'s tailwind-merge resolves a conflict last-one-wins,
 * and `lib/utils.ts` registers the px-named type scale into the `font-size`
 * class group so `text-12-5px` correctly beats the base `text-sm`. Anything that
 * must beat a `size` step (a tighter padding) therefore goes in
 * `compoundVariants`, which is the same reason `ink` lives there.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/70 disabled:pointer-events-none disabled:opacity-50 gap-1 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'outline-solid':
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        /**
         * The quiet inline text affordance: a `Clear`, a `Dismiss`. Ghost-like
         * with no fill, muted until hovered, and deliberately inboard of its
         * size step's padding because it reads as text rather than as a
         * control. Both filter toolbars had hand-written it as
         * `ghost` + four classes.
         */
        quiet: 'text-muted-foreground hover:text-foreground-secondary',
        /**
         * The card-like button: a panel that happens to be pressable, rather
         * than a control drawn on a panel. It was `bg-card border-border
         * hover:bg-surface-subtle` at every call site that wanted it, which is
         * a variant wearing a class string.
         */
        surface: 'border border-border bg-card hover:bg-surface-subtle',
        /** The inverted primary: dark fill, light label. */
        inverse: 'bg-foreground text-background hover:bg-foreground/90',
        /**
         * The beneficiary portal's forward CTA. Its blue is the portal's own
         * brand rather than the app's primary, because the portal is the one
         * surface a member of the public sees and it is deliberately not
         * dressed as the firm's software.
         */
        // VALE-1230: this blue is deliberately NOT a semantic token. The token
        // set follows the app's theme and this surface must not: a beneficiary
        // opens the portal from an email, on their own device, and it is
        // dressed as correspondence from the firm rather than as the firm's
        // software. Making it themeable would let a future theme change
        // repaint a page no user of the app ever sees.
        // eslint-disable-next-line vale/no-raw-palette-colour
        portal: 'bg-blue-600 text-white hover:bg-blue-700',
        /**
         * The portal's back affordance. Quiet, and it drops its hover fill
         * entirely at `sm` and up, where the step layout gives it room.
         */
        // VALE-1230: same surface, same reason as `portal` above.
        // eslint-disable-next-line vale/no-raw-palette-colour
        'portal-back': 'hover:bg-gray-100 sm:hover:bg-transparent',
        /** The public marketing report CTA, on its own deep-green brand. */
        // VALE-1230: the public report landing page runs on its own marketing
        // palette, set by the campaign rather than by the product theme.
        // eslint-disable-next-line vale/no-raw-palette-colour
        marketing: 'bg-[#244643] text-white hover:bg-[#1a3532]',
        /**
         * The mobile hand-off hero: the single full-width action on a phone
         * screen, lifted off the page so it reads as the only thing to press.
         */
        'mobile-hero':
          'bg-success text-success-foreground hover:bg-success/90 shadow-[0_8px_24px_rgba(16,185,129,0.35)] active:scale-[0.98]',
        /** The details sidebar rail: a card that happens to be pressable. */
        rail: 'bg-card shadow-[0_0_2px_0_rgba(28,40,64,0.18),0_1px_3px_0_rgba(24,41,75,0.04)]',
        /**
         * A button that must read as a form input: the trigger of a field-shaped
         * control, sharing the input background so the two do not disagree.
         */
        'faux-input': 'border-0 bg-[var(--input-bg)]',
        /**
         * A quieter `outline`: its edge and hover are drawn from the foreground
         * at low alpha rather than from `--input` and `--accent`, so it recedes
         * on a busy panel where a full-strength outline would compete. Used by
         * the QR hand-off screens and the recording modal.
         *
         * NOT an inverse variant, despite being called `outline-subtle` when
         * it was first extracted (VALE-1230). Nothing about it is inverted: it
         * sits on the ordinary background and takes the ordinary foreground.
         * The misnaming produced a Storybook demo that rendered it on a dark
         * panel, black on black, which is how it was caught.
         */
        'outline-subtle':
          'border border-foreground/20 bg-transparent text-foreground hover:bg-foreground/10',
        ink: 'rounded-md bg-linear-to-b from-foreground to-foreground-strong font-semibold text-background shadow-sm ring-1 ring-foreground/40 ring-inset transition-all hover:from-foreground/90 hover:to-foreground-strong disabled:opacity-60'
      },
      size: {
        default: 'py-[0.1875rem] px-[0.375rem]',
        xs: 'h-8 rounded-sm px-2.5',
        sm: 'h-9 rounded-sm px-3',
        lg: 'h-11 rounded-sm px-8',
        /**
         * ── THE ICON-SQUARE LADDER, in px, because it is not inferable ──────
         *   icon      40   icon-sm  32   icon-xs  28
         *   icon-2xs  24   icon-3xs 20
         *
         * `icon-xs` is 28 and PREDATES this ladder, which is why it sits off
         * the text scale's step (text `xs` is 32). It is not renamed to fit,
         * because renaming it would silently resize every call site already
         * using it. New steps are named around it.
         */
        icon: 'h-10 w-10 p-0',
        'icon-xs': 'h-7 w-7 p-0',
        /** 32px. 54 call sites, the most common icon button in the tree. */
        'icon-sm': 'h-8 w-8 p-0',
        /** 24px. The dense row action. */
        'icon-2xs': 'h-6 w-6 p-0',
        /** 20px. The inline dismiss sitting inside a line of text. */
        'icon-3xs': 'h-5 w-5 p-0',
        /**
         * h-7, the dense row/inline control, 92 call sites. Padding is `px-3`
         * rather than something tighter because that is what every h-7 call
         * site actually rendered: they were written as `size="sm"` with the
         * height overridden, so they kept `sm`'s padding.
         */
        '2xs': 'h-7 rounded-sm px-3',
        /** h-6, the tightest chip-scale control. 25 call sites. */
        '3xs': 'h-6 rounded-sm px-1.5',
        /** No fixed height: the button wraps its content (37 call sites). */
        auto: 'h-auto rounded-sm px-2 py-1',
        /** h-10, between `sm` and `lg`. */
        md: 'h-10 rounded-sm px-3',
        /** h-5, the inline control sitting in a line of text. */
        '4xs': 'h-5 rounded-sm px-1.5',
        /** h-4, the count badge that is also pressable. */
        '5xs': 'h-4 rounded-sm px-0.5',
        /** h-14, the mobile hero. The largest step, and the only one over 44. */
        xl: 'h-14 rounded-sm px-8',
        /** 36px and 16px icon squares, completing the ladder. */
        'icon-md': 'h-9 w-9 p-0',
        'icon-4xs': 'h-4 w-4 p-0',
        /**
         * `h-[32px]`, literal — deliberately the ONLY size step NOT expressed
         * as `h-N` (VALE-1234). Every other step here is `calc(var(--spacing)
         * * N)`, so it shrinks with the app-wide 0.85× density knob
         * (`--spacing` in `app/globals.css`) by design; `xs`'s `h-8` renders
         * 27.2px under it, not 32. This step is for a control whose height is
         * a fixed accessibility target rather than a density-scaled visual
         * choice — the header-mounted `AttributeDrawerDelete` and its
         * mirrored `Merge` button hold a 32px hit box to WCAG 2.2 SC 2.5.8,
         * which must not move when the density knob does.
         */
        'target-32': 'h-[32px] rounded-sm px-2.5'
      },
      /**
       * Type scale only. See the header on why this is not also the gap.
       *
       * The steps are the sizes call sites actually asked for, measured. Note
       * `text-xs` and `text-12px` render the same 12px, and `text-sm` is the
       * base, so both of those collapse into steps here rather than surviving
       * as separate class strings.
       */
      density: {
        /** 14px, the cva base. */
        default: '',
        /** 16px, the large dialog/portal CTA. */
        large: 'text-base',
        /** 13px. */
        mid: 'text-13px',
        /** 12.5px, the house compact register. */
        compact: 'text-12-5px',
        /** 12px, the confirm-strip / micro-control register. */
        micro: 'text-12px',
        /** 11px, the tightest legend-scale control. */
        nano: 'text-11px',
        /** 11.5px, a half-px legacy size the type scale still carries. */
        'nano-plus': 'text-11-5px'
      },
      /**
       * Horizontal padding, as an OVERRIDE on the one `size` already gives.
       *
       * It exists because the measurement said it has to. Across the shipped
       * call sites, padding varies INDEPENDENTLY of height: `h-7` alone ships
       * with `px-3` (34 sites), `px-2` (15), `px-2.5` (3), the base padding (2)
       * and `px-0` (1). Thirty distinct height-and-padding pairs appear over
       * 156 sites.
       *
       * The alternative was a size step per pair, which is thirty steps whose
       * names would carry no meaning beyond the Tailwind values they wrap. Two
       * small axes express the same set and stay readable.
       *
       * `default` is empty so every existing call site keeps whatever its `size`
       * step already gives it. This axis only ever appears where a call site
       * deliberately departs from that.
       */
      pad: {
        default: '',
        /** No padding at all, both axes. */
        flush: 'p-0',
        /** Horizontal only, from tightest to widest. */
        none: 'px-0',
        '3xs': 'px-0.5',
        '2xs': 'px-1',
        xs: 'px-1.5',
        sm: 'px-2',
        md: 'px-2.5',
        lg: 'px-3',
        xl: 'px-4',
        '2xl': 'px-6',
        '3xl': 'px-8',
        /** Uniform, for a button that wraps a block of content. */
        block: 'p-1',
        'block-lg': 'p-2'
      },
      /** Vertical padding, an override exactly as `pad` is for horizontal. */
      padY: {
        default: '',
        none: 'py-0',
        '3xs': 'py-0.5',
        '2xs': 'py-1',
        xs: 'py-1.5',
        sm: 'py-2'
      },
      /**
       * Width. `full` is the common one; `fit` and `auto` appear where a button
       * sits in a grid that would otherwise stretch it.
       */
      width: {
        default: '',
        full: 'w-full',
        fit: 'w-fit',
        auto: 'w-auto'
      },
      /**
       * The row-hover affordance: invisible until its group is hovered or it
       * takes focus. Always both, because keyboard users cannot hover.
       */
      reveal: {
        true: 'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
        false: ''
      },
      /**
       * Corner radius. The base is `rounded-sm`, and a handful of call sites
       * legitimately depart: a segmented control flattens the edge it joins on,
       * a pill is fully round. Those were classes; they are steps now.
       */
      radius: {
        default: '',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
        /** Segmented control: flatten the edge that meets its neighbour. */
        'flat-left': 'rounded-l-none',
        'flat-right': 'rounded-r-none'
      },
      /**
       * Joining a segmented control does three things at once, which is why it
       * is one step rather than three classes: the meeting edge flattens, the
       * doubled border collapses, and the focus ring stops drawing down the
       * seam where it would read as a crack.
       */
      join: {
        default: '',
        left: 'rounded-l-none border-l-0 focus-visible:ring-0',
        right: 'rounded-r-none border-r-0 focus-visible:ring-0'
      },
      /** Icon-to-label spacing. Exactly three values are in use. */
      gap: {
        /** The cva base's own `gap-1`. */
        tight: '',
        /** 150 call sites: the house icon+label pairing. */
        standard: 'gap-1.5',
        /** 49 call sites: roomier, used where the label is long. */
        loose: 'gap-2'
      },
      /**
       * Foreground colour and its hover partner, as one step. They are declared
       * together because that is how they were always hand-written: every one of
       * the 74 `text-muted-foreground` call sites also carried a `hover:` class,
       * and the ones that drifted drifted by losing the hover half.
       */
      tone: {
        default: '',
        muted: 'text-muted-foreground hover:text-foreground-secondary',
        secondary: 'text-foreground-secondary hover:text-foreground',
        subtle: 'text-foreground-subtle hover:text-foreground-secondary',
        destructive: 'text-destructive hover:bg-destructive/10',
        'danger-subtle':
          'text-error-subtle-foreground hover:bg-error-subtle hover:text-error-subtle-strong-foreground',
        /** Full-strength body colour, for a control that must not recede. */
        strong: 'text-foreground hover:text-foreground',
        /** The link-coloured action sitting inside body copy. */
        accent: 'text-primary hover:text-primary/80',
        info: 'text-info-subtle-foreground hover:bg-info-subtle',
        /**
         * The (?) beside a title (GUIDE-19): quiet at rest, info-blue while the
         * whole hit box is hovered, focused or holding its popover open. A named
         * step rather than `group` classes on the glyph, which the shipped
         * button refuses.
         */
        hint: 'text-foreground-subtle hover:bg-transparent hover:text-info focus-visible:text-info data-[state=open]:text-info',
        success: 'text-success-subtle-foreground hover:bg-success-subtle',
        warning: 'text-warning-subtle-foreground hover:bg-warning-subtle',
        /**
         * Hover FILLS rather than recolours: the label holds its colour and a
         * muted panel appears behind it. A distinct interaction, not a shade.
         */
        'muted-fill':
          'text-muted-foreground hover:bg-muted hover:text-muted-foreground',
        'secondary-fill':
          'text-foreground-secondary hover:bg-muted hover:text-foreground-secondary',
        /** Holds its colour through hover, for a label on a surface button. */
        'secondary-hold':
          'text-foreground-secondary hover:text-foreground-secondary',
        /**
         * A dimmed foreground that stays dimmed through hover: the lesser of
         * two adjacent actions, where a colour change would read as the more
         * important one. Also not inverse; see `outline-subtle`.
         */
        'foreground-dim': 'text-foreground/60 hover:text-foreground/60',
        /** Muted until hovered, then destructive. The row-level delete. */
        'muted-danger':
          'text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:text-destructive',
        /** The success affordance that dims rather than greys when disabled. */
        'success-quiet':
          'text-success-subtle-foreground hover:bg-success-subtle disabled:text-foreground-subtle',
        /** A warning that resolves to full strength on hover. */
        'warning-quiet': 'text-warning/70 hover:text-warning-subtle-foreground',
        /** Fills to the primary on hover, for a row-level confirm. */
        'accent-fill': 'hover:bg-primary hover:text-primary-foreground'
      },
      /** Content alignment inside the button. Base is `justify-center`. */
      align: {
        center: '',
        start: 'justify-start',
        between: 'justify-between'
      },
      /** Font weight. Base is `font-medium`. */
      weight: {
        default: '',
        normal: 'font-normal',
        semibold: 'font-semibold',
        bold: 'font-bold'
      },
      /**
       * The selected state of a filter chip: both toolbars express "this filter
       * is active" this way, and it is a state of the control, not a colour
       * choice at the call site.
       */
      selected: {
        true: '',
        false: ''
      }
    },
    compoundVariants: [
      /** Dialog-footer primary — no fixed height, `px-3 py-1.5`. */
      {
        variant: 'ink',
        size: 'default',
        class: 'rounded-md gap-1.5 px-3 py-1.5 text-12-5px'
      },
      /** Header / inline-control primary — `h-8 px-3`. */
      {
        variant: 'ink',
        size: 'xs',
        class: 'rounded-md gap-1.5 px-3 text-12-5px'
      },
      /** Banner CTA — `h-9 px-4`. */
      {
        variant: 'ink',
        size: 'sm',
        class: 'rounded-md gap-1.5 px-4 text-12-5px'
      },
      /**
       * `quiet` sits inboard of every size step's padding. In `compoundVariants`
       * because `variants` cannot beat `size`, which sets the padding it has to
       * override. That is the same constraint `ink` is here for.
       */
      { variant: 'quiet', size: 'xs', class: 'px-2' },
      { variant: 'quiet', size: '2xs', class: 'px-1.5' },
      { variant: 'quiet', size: 'sm', class: 'px-2' },
      /**
       * The selected filter chip. Compound with `outline` because the state has
       * to beat that variant's own border and background.
       */
      {
        variant: 'outline',
        selected: true,
        class: 'border-info/40 bg-info-subtle'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      density: 'default',
      pad: 'default',
      padY: 'default',
      width: 'default',
      reveal: false,
      radius: 'default',
      join: 'default',
      gap: 'tight',
      tone: 'default',
      align: 'center',
      weight: 'default',
      selected: false
    }
  }
);

/**
 * `className` is POLICED (VALE-1202). A class may never set a concept an axis
 * owns: colour, padding, height, width, type scale, gap, radius, weight,
 * alignment. That is how the design leaked out of `buttonVariants` in the first
 * place, and the leak is measurable: the reference CTA's `text-12-5px gap-1.5`
 * reached seven unrelated feature areas as a hand-copied string, and the eighth
 * copy had already lost half of it.
 *
 * Anything else is the call site's business, because it was never a button
 * style: where the button sits in its parent, what it does at a breakpoint, a
 * transform.
 *
 *   <Button className="mt-4 shrink-0">        ok, neither is a button style
 *   <Button pad="sm">                 ✗ that is `size`
 *   <Button tone="muted">✗ that is `tone`
 *   <Button className="sm:w-auto">            ok, no axis can express a breakpoint
 *
 * `components/ui/button-class-policy.ts` holds the rule and the reasoning. The
 * check runs on the string literal you pass, token by token, and the compile
 * error names the prop to reach for instead. A `cn(...)` call or a variable is
 * typed `string`, which a conditional type cannot inspect, so
 * `vale/no-classname-on-button` covers that blind spot.
 *
 * THERE IS NO ESCAPE HATCH, deliberately. A `classNameTestOnly` prop existed
 * briefly and was deleted: it was introduced to let a 1,295-site rename land
 * without converting every call site, and the effect was that 1,520 banned
 * concept tokens kept shipping through a prop the policy did not inspect. A
 * hatch that the debt can live in is not a hatch, it is the old behaviour with
 * a new name. If a treatment cannot be expressed here, add the named step.
 *
 * `asChild` composition is unaffected: a class on the CHILD element is the
 * child's own, and neither this type nor the lint rule touches it.
 */
export interface ButtonProps<TClass extends string = string>
  extends
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Layout, breakpoints and transforms only. A class naming a concept an axis
   * owns is a compile error: see `button-class-policy.ts`.
   */
  className?: CheckClassName<TClass>;
}

const Button = <TClass extends string = string>({
  ref,
  className,
  variant,
  size,
  density,
  pad,
  padY,
  width,
  reveal,
  radius,
  join,
  gap,
  tone,
  align,
  weight,
  selected,
  asChild = false,
  ...props
}: ButtonProps<TClass> & { ref?: React.Ref<HTMLButtonElement> }) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(
        buttonVariants({
          variant,
          size,
          density,
          pad,
          padY,
          width,
          reveal,
          radius,
          join,
          gap,
          tone,
          align,
          weight,
          selected,
          className: className as string | undefined
        })
      )}
      ref={ref}
      {...props}
    />
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
